import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import apiService from '../services/apiService';

const PLACEHOLDERS = [
  'client_name', 'share_url', 'estimate_id', 'total', 'currency', 'valid_until',
  'job_name', 'scheduled_date', 'scheduled_time', 'address', 'review_url', 'company_name',
];

export default function MessageTemplates() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    channel: 'email',
    name: '',
    body: '',
    active: true,
  });
  const [suggestJobId, setSuggestJobId] = useState('');
  const [suggestEstimateId, setSuggestEstimateId] = useState('');
  const [suggestBusy, setSuggestBusy] = useState(false);

  async function fetchItems() {
    setError('');
    setLoading(true);
    try {
      const res = await apiService.get('message-templates?include_inactive=1');
      setItems(Array.isArray(res) ? res : []);
    } catch (e) {
      setError(e.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openCreate() {
    setEditingId(null);
    setForm({ channel: 'email', name: '', body: '', active: true });
    setSuggestJobId('');
    setSuggestEstimateId('');
    setDialogOpen(true);
  }

  async function openEdit(id) {
    setError('');
    try {
      const t = await apiService.get(`message-templates/${id}`);
      setEditingId(id);
      setForm({
        channel: t.channel === 'sms' ? 'sms' : 'email',
        name: t.name || '',
        body: t.body || '',
        active: t.active !== false,
      });
      setDialogOpen(true);
    } catch (e) {
      setError(e.message || 'Failed to load template');
    }
  }

  async function handleSuggestAi() {
    setError('');
    setSuggestBusy(true);
    try {
      const payload = {
        polish: true,
      };
      if (editingId) {
        payload.message_template_id = editingId;
      } else {
        payload.body = form.body;
        payload.channel = form.channel;
      }
      if (suggestJobId.trim()) payload.collection_id = Number(suggestJobId);
      if (suggestEstimateId.trim()) payload.estimate_id = Number(suggestEstimateId);

      const res = await apiService.post('ai/suggest-message', payload);
      if (res?.body) {
        setForm((f) => ({ ...f, body: res.body }));
      }
    } catch (e) {
      setError(e.message || 'AI suggest failed');
    } finally {
      setSuggestBusy(false);
    }
  }

  async function handleSave() {
    setError('');
    setBusy(true);
    try {
      const payload = {
        channel: form.channel,
        name: form.name.trim(),
        body: form.body,
        active: !!form.active,
      };
      if (!payload.name) {
        setError('Name is required');
        setBusy(false);
        return;
      }
      if (!payload.body.trim()) {
        setError('Body is required');
        setBusy(false);
        return;
      }
      if (editingId) {
        await apiService.put(`message-templates/${editingId}`, payload);
      } else {
        await apiService.post('message-templates', payload);
      }
      setDialogOpen(false);
      setEditingId(null);
      await fetchItems();
    } catch (e) {
      setError(e.message || 'Failed to save template');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this template?')) return;
    setError('');
    try {
      await apiService.delete(`message-templates/${id}`);
      await fetchItems();
    } catch (e) {
      setError(e.message || 'Failed to delete');
    }
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Message templates
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none' }}>
          New template
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        SMS and email drafts with placeholders such as <code>{'{{share_url}}'}</code>,{' '}
        <code>{'{{client_name}}'}</code>, <code>{'{{total}}'}</code>, <code>{'{{currency}}'}</code>,{' '}
        <code>{'{{estimate_id}}'}</code>, <code>{'{{valid_until}}'}</code>. Use “Draft message” on Estimates after generating a share link.
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 2, display: 'block' }}>
        Keys: {PLACEHOLDERS.join(', ')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.length === 0 ? (
            <Card>
              <CardContent>
                <Typography sx={{ fontWeight: 800 }}>No templates yet.</Typography>
                <Typography variant="body2" color="text.secondary">
                  Create email or SMS drafts for client outreach.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            items.map((it) => (
              <Card key={it.id} sx={{ boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900 }}>{it.name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      <Chip size="small" label={it.channel === 'sms' ? 'SMS' : 'Email'} sx={{ mr: 1, height: 22 }} />
                      {!it.active ? <Chip size="small" label="Inactive" color="default" sx={{ height: 22 }} /> : null}
                    </Typography>
                  </Box>
                  <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEdit(it.id)} sx={{ textTransform: 'none' }}>
                    Edit
                  </Button>
                  <IconButton aria-label="Delete" onClick={() => handleDelete(it.id)}>
                    <DeleteIcon />
                  </IconButton>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => !busy && setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800 }}>{editingId ? `Edit template #${editingId}` : 'New message template'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="msg-tpl-channel">Channel</InputLabel>
              <Select
                labelId="msg-tpl-channel"
                label="Channel"
                value={form.channel}
                onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}
              >
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
          </Box>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Switch checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />}
            label="Active"
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
            <TextField
              label="Job ID (optional context)"
              value={suggestJobId}
              onChange={(e) => setSuggestJobId(e.target.value)}
              placeholder="e.g. 42"
              fullWidth
            />
            <TextField
              label="Estimate ID (optional context)"
              value={suggestEstimateId}
              onChange={(e) => setSuggestEstimateId(e.target.value)}
              placeholder="e.g. 7"
              fullWidth
            />
          </Box>
          <TextField
            label="Body"
            value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            fullWidth
            multiline
            minRows={8}
            sx={{ mt: 2 }}
            placeholder={`Example:\nHi {{client_name}},\n\nPlease review your estimate: {{share_url}}\n\nTotal: {{total}} {{currency}}`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={busy || suggestBusy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="outlined" onClick={handleSuggestAi} disabled={busy || suggestBusy} sx={{ textTransform: 'none' }}>
            {suggestBusy ? 'Suggesting…' : 'Suggest with AI'}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={busy || suggestBusy} sx={{ textTransform: 'none' }}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
